/**
 * NinaForm — shared component para alta + edit.
 *
 * Per FWA-PWA-023 G0 D2: Zod resolver con NinaCreatePayloadSchema (create)
 * o NinaUpdatePayloadSchema (edit). RHF mode onBlur.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type NinaCreatePayload,
  NinaCreatePayloadSchema,
  type NinaUpdatePayload,
  NinaUpdatePayloadSchema,
} from '@sisrep/schemas';
import { useForm } from 'react-hook-form';
import { Button } from './ui/Button.js';
import { Input } from './ui/Input.js';

interface NinaFormCreateProps {
  mode: 'create';
  initialValues?: Partial<NinaCreatePayload>;
  onSubmit: (payload: NinaCreatePayload) => void;
  isSubmitting?: boolean;
  serverError?: string;
}

interface NinaFormEditProps {
  mode: 'edit';
  initialValues: Partial<NinaUpdatePayload>;
  onSubmit: (payload: NinaUpdatePayload) => void;
  isSubmitting?: boolean;
  serverError?: string;
}

type NinaFormProps = NinaFormCreateProps | NinaFormEditProps;

export function NinaForm(props: NinaFormProps) {
  if (props.mode === 'create') {
    return <NinaFormCreate {...props} />;
  }
  return <NinaFormEdit {...props} />;
}

// ─── Create variant ───────────────────────────────────────────────────────

function NinaFormCreate({
  initialValues,
  onSubmit,
  isSubmitting,
  serverError,
}: NinaFormCreateProps) {
  // Cast resolver para evitar conflicto exactOptionalPropertyTypes con RHF/Zod generics
  // biome-ignore lint/suspicious/noExplicitAny: RHF + zodResolver + exactOptionalPropertyTypes TS friction
  const resolver = zodResolver(NinaCreatePayloadSchema) as any;
  const {
    register,
    handleSubmit,
    formState: { errors },
    // biome-ignore lint/suspicious/noExplicitAny: see above
  } = useForm<any>({
    resolver,
    mode: 'onBlur',
    defaultValues: initialValues,
  });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data as NinaCreatePayload))}
      className="space-y-4"
    >
      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Input
        label="Nombre completo"
        {...register('nombre_completo')}
        error={errors.nombre_completo?.message as string | undefined}
      />
      <Input
        label="Fecha de nacimiento"
        type="date"
        {...register('fecha_nacimiento')}
        error={errors.fecha_nacimiento?.message as string | undefined}
      />
      <Input
        label="Folio GDM"
        {...register('folio_gdm')}
        error={errors.folio_gdm?.message as string | undefined}
      />
      <Input
        label="Tutor — nombre"
        {...register('tutor_nombre')}
        error={errors.tutor_nombre?.message as string | undefined}
      />
      <Input
        label="Tutor — email"
        type="email"
        {...register('tutor_email')}
        error={errors.tutor_email?.message as string | undefined}
      />
      <Input
        label="Tutor — teléfono"
        type="tel"
        {...register('tutor_telefono')}
        error={errors.tutor_telefono?.message as string | undefined}
      />
      <Input
        label="Rama actual"
        {...register('rama_actual')}
        error={errors.rama_actual?.message as string | undefined}
      />
      <Input
        label="Fecha de alta"
        type="date"
        {...register('fecha_alta')}
        error={errors.fecha_alta?.message as string | undefined}
      />

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Crear niña'}
        </Button>
      </div>
    </form>
  );
}

// ─── Edit variant (partial fields) ────────────────────────────────────────

function NinaFormEdit({ initialValues, onSubmit, isSubmitting, serverError }: NinaFormEditProps) {
  // biome-ignore lint/suspicious/noExplicitAny: RHF + zodResolver + exactOptionalPropertyTypes TS friction
  const resolver = zodResolver(NinaUpdatePayloadSchema) as any;
  const {
    register,
    handleSubmit,
    formState: { errors },
    // biome-ignore lint/suspicious/noExplicitAny: see above
  } = useForm<any>({
    resolver,
    mode: 'onBlur',
    defaultValues: initialValues,
  });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data as NinaUpdatePayload))}
      className="space-y-4"
    >
      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Input
        label="Nombre completo"
        {...register('nombre_completo')}
        error={errors.nombre_completo?.message as string | undefined}
      />
      <Input
        label="Tutor — nombre"
        {...register('tutor_nombre')}
        error={errors.tutor_nombre?.message as string | undefined}
      />
      <Input
        label="Tutor — email"
        type="email"
        {...register('tutor_email')}
        error={errors.tutor_email?.message as string | undefined}
      />
      <Input
        label="Tutor — teléfono"
        type="tel"
        {...register('tutor_telefono')}
        error={errors.tutor_telefono?.message as string | undefined}
      />
      <Input
        label="Rama actual"
        {...register('rama_actual')}
        error={errors.rama_actual?.message as string | undefined}
      />

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </div>
    </form>
  );
}
